import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAuth0 } from '@ondeck/silkworm';
import { useForm } from 'react-hook-form';
import PartnerSupport from 'components/PartnerSupport';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Row, Col, Button, Card, Form, Alert } from 'react-bootstrap';
import { useAppData } from 'contexts/appContext';

interface ForgotPasswordFormInputs {
  emailAddress: string;
}

const ForgotPasswordPage = () => {
  const { changePassword } = useAuth0();
  const [isVisible, setIsVisible] = useState(false);

  const { hideLogin } = useAppData();
  useEffect(() => {
    hideLogin();
  });

  const schema = Yup.object().shape({
    emailAddress: Yup.string().required().email()
  });

  const {
    register,
    formState: { errors },
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  const onSubmit = (data: ForgotPasswordFormInputs) => {
    const { emailAddress } = data;
    changePassword(emailAddress);
    setIsVisible(true);
  };

  return (
    <>
      <Alert
        variant={'success'}
        show={isVisible}
        onClose={() => setIsVisible(false)}
        dismissible
      >
        <FormattedMessage id="forgotPassword.banner.message" />
      </Alert>
      <Card>
        <Card.Body>
          <Row>
            <Col xs={12} md={8}>
              <h1 className="fw-400">
                <FormattedMessage id="forgotPassword.header" />
              </h1>
              <p className="pt-3">
                <FormattedMessage id="forgotPassword.info.text" />
              </p>
              <Form className="mt-4">
                <Form.Group className="mb-4">
                  <Form.Label className="fs-4 pb-1">Email Address</Form.Label>
                  <Form.Control
                    data-testid="email"
                    autoFocus
                    {...register('emailAddress', { required: true })}
                    type="email"
                  />
                  <span className="text-danger fs-5" data-testid="valid">
                    {errors?.emailAddress?.type === 'email' && (
                      <FormattedMessage id="email.invalidEntry.message" />
                    )}
                    {errors?.emailAddress?.type === 'required' && (
                      <FormattedMessage id="required.message" />
                    )}
                  </span>
                </Form.Group>
              </Form>
              <Button
                onClick={handleSubmit(onSubmit)}
                className="me-4"
                variant="primary"
                type="submit"
                data-testid="submit"
              >
                <FormattedMessage id="forgotPassword.resetLink" />
              </Button>
              <a href="/login" className="text-decoration-none">
                <FormattedMessage id="forgotPassword.backToSignIn" />
              </a>
            </Col>
            <Col md={4} className="d-none d-md-block text-center">
              <PartnerSupport />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default ForgotPasswordPage;
